import React, { forwardRef } from "react"

import { ImageFormat, ImagePreset, optimizeImageUrl } from "../../utils/image"

export type DatoCmsImageUrl =
  `https://www.datocms-assets.com/${number}/${string}.${ImageFormat}`

export interface OptimizedImageProps
  extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: DatoCmsImageUrl
  alt: string | undefined
  width?: number | string
  height?: number | string

  /**
   * Image preset gives predefined quality settings
   * @default "editorial" (conservative, high quality setting)
   */
  preset: ImagePreset

  /** Override quality setting (1-100) from preset default */
  qualityOverride?: number

  /**
   * Enable WebP/AVIF format conversion
   * @default true
   */
  autoFormat?: boolean

  /**
   * Enable automatic compression
   * @see https://docs.imgix.com/en-US/apis/rendering/automatic
   * @default true
   */
  autoCompress?: boolean

  /**
   * Loading strategy (eager for above-the-fold, lazy for below)
   * @default "lazy"
   */
  loading?: "lazy" | "eager"

  /**
   * Device Pixel Ratio (1-5) for responsive images
   * @see https://docs.imgix.com/en-US/apis/rendering/device-pixel-ratio
   * @default 1
   *
   */
  dpr?: number

  /**
   * Resize fit mode for the image
   * @see https://docs.imgix.com/en-US/apis/rendering/size/resize-fit-mode
   */
  fit?:
    | "clip"
    | "clamp"
    | "crop"
    | "facearea"
    | "fill"
    | "fillmax"
    | "max"
    | "min"
    | "scale"
}

/**
 * OptimizedImage applies Imgix optimization parameters to
 * a given DatoCMS image URL to deliver a performant image, consistently.
 * It allows us to avoid query string pollution of datocms-assets.com URLs.
 *
 * Compare to `<DatoImage />`, which is responsible for providing inline,
 * base64-encoded approximations of images, responsive images (srcset),
 * and "last mile" web-vitals optimization.
 */
const OptimizedImage = forwardRef<HTMLImageElement, OptimizedImageProps>(
  (props, ref) => {
    const {
      src,
      alt,
      width,
      height,
      preset,
      qualityOverride,
      autoFormat = true,
      autoCompress = true,
      loading = "lazy",
      dpr = 1,
      fit,
      ...rest
    } = props

    const optimizedSrc = optimizeImageUrl(src, {
      preset,
      quality: qualityOverride,
      autoFormat,
      autoCompress,
      dpr,
      fit,
    })

    return (
      <img
        ref={ref}
        src={optimizedSrc}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        {...rest}
      />
    )
  },
)

OptimizedImage.displayName = "OptimizedImage"

export default OptimizedImage
